import React, { useState, useMemo } from 'react';
import { ChevronDown, ChevronRight, AlertCircle, CheckCircle, AlertTriangle } from 'lucide-react';
import { DiffErrorBoundary } from './DiffErrorBoundary';
import { DiffEditor } from '@monaco-editor/react';
import './CodeDiffViewer.css';

interface CodeChange {
  line_start: number;
  line_end: number;
  original_code: string;
  adapted_code: string;
  change_type: string;
  reason: string;
  risk_level: string;
}

interface CodeDiffViewerProps {
  originalCode: string;
  adaptedCode: string;
  changes: CodeChange[];
  preservationRatio: number;
  visualDiff?: any[];
  language?: string; // Optional language hint
}

// Helper function to detect language from code content
const detectLanguage = (code: string): string => {
  // Check for common language patterns
  if (code.includes('import pandas') || code.includes('import numpy') || code.includes('def ') || code.includes('print(')) {
    return 'python';
  }
  if (code.includes('SELECT ') || code.includes('FROM ') || code.includes('WHERE ') || code.includes('INSERT INTO')) {
    return 'sql';
  }
  if (code.includes('function ') || code.includes('const ') || code.includes('let ') || code.includes('var ') || code.includes('=>')) {
    return 'javascript';
  }
  if (code.includes('interface ') || code.includes('type ') || code.includes(': string') || code.includes(': number')) {
    return 'typescript';
  }
  if (code.includes('<?php') || code.includes('echo ')) {
    return 'php';
  }
  if (code.includes('#include') || code.includes('int main') || code.includes('std::')) {
    return 'cpp';
  }
  if (code.includes('public class') || code.includes('public static void') || code.includes('System.out.')) {
    return 'java';
  }
  if (code.includes('package main') || code.includes('func ')) {
    return 'go';
  }
  if (code.includes('fn ') || code.includes('let mut') || code.includes('impl ')) {
    return 'rust';
  }
  // Default to python as it's most common in this app
  return 'python';
};

const CodeDiffViewerContent: React.FC<CodeDiffViewerProps> = ({
  originalCode,
  adaptedCode,
  changes,
  preservationRatio: propPreservationRatio,
  visualDiff,
  language
}) => {
  const [expandedSections, setExpandedSections] = useState<Set<number>>(new Set());
  const [viewMode, setViewMode] = useState<'unified' | 'split' | 'aligned'>('aligned');
  
  // Calculate diff statistics for display
  const diffStats = useMemo(() => {
    try {
      // Simple line-based diff for statistics
      const originalLines = originalCode.split('\n');
      const adaptedLines = adaptedCode.split('\n');
      
      // Basic statistics (Monaco handles the actual diff display)
      const minLines = Math.min(originalLines.length, adaptedLines.length);
      
      // Rough estimation of changes
      let additions = 0;
      let deletions = 0;
      let modifications = 0;
      
      if (adaptedLines.length > originalLines.length) {
        additions = adaptedLines.length - originalLines.length;
      } else if (originalLines.length > adaptedLines.length) {
        deletions = originalLines.length - adaptedLines.length;
      }
      
      // Count modified lines (simplified)
      for (let i = 0; i < minLines; i++) {
        if (originalLines[i] !== adaptedLines[i]) {
          modifications++;
        }
      }
      
      return {
        originalLineCount: originalLines.length,
        adaptedLineCount: adaptedLines.length,
        additions,
        deletions,
        modifications
      };
    } catch (error) {
      // Return empty result on error
      return {
        originalLineCount: 0,
        adaptedLineCount: 0,
        additions: 0,
        deletions: 0,
        modifications: 0
      };
    }
  }, [originalCode, adaptedCode]);
  
  // Calculate preservation ratio from diff
  const preservationRatio = useMemo(() => {
    if (propPreservationRatio) return propPreservationRatio;
    
    // Simple preservation calculation
    const { originalLineCount, modifications, deletions } = diffStats;
    if (originalLineCount === 0) return 100;
    
    const unchangedLines = originalLineCount - deletions - modifications;
    const preservedLines = unchangedLines + (modifications * 0.5); // Count modified as 50% preserved
    
    return Math.round((preservedLines / originalLineCount) * 100);
  }, [propPreservationRatio, diffStats]);

  const toggleSection = (index: number) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedSections(newExpanded);
  };

  const getRiskIcon = (riskLevel: string) => {
    switch (riskLevel) {
      case 'high':
        return <AlertTriangle className="risk-icon high" size={16} />;
      case 'medium':
        return <AlertCircle className="risk-icon medium" size={16} />;
      default:
        return <CheckCircle className="risk-icon low" size={16} />;
    }
  };

  const getChangeTypeLabel = (changeType: string) => {
    return changeType.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  const renderAlignedDiff = () => {
    // Detect language if not provided
    const detectedLanguage = language || detectLanguage(originalCode) || detectLanguage(adaptedCode);
    
    // Use Monaco DiffEditor for proper alignment like VS Code
    return (
      <div className="monaco-diff-container" style={{ 
        height: 'calc(100vh - 300px)', // Dynamic height based on viewport
        minHeight: '400px',
        maxHeight: '800px',
        border: '1px solid var(--color-gray-200)', 
        borderRadius: '8px',
        overflow: 'hidden'
      }}>
        <DiffEditor
          original={originalCode}
          modified={adaptedCode}
          language={detectedLanguage}
          theme="light"
          options={{
            renderSideBySide: true,
            readOnly: true,
            wordWrap: "off",
            diffAlgorithm: "advanced",       // enables smarter alignment / move detection
            ignoreTrimWhitespace: true,      // reduces noisy diffs
            renderIndicators: true,
            minimap: { enabled: false },
            scrollBeyondLastLine: false,
            fontSize: 13,
            fontFamily: 'var(--font-mono)',
            renderWhitespace: 'none',
            automaticLayout: true,
            scrollbar: {
              vertical: 'visible',
              horizontal: 'visible',
              verticalScrollbarSize: 12,
              horizontalScrollbarSize: 12
            }
          }}
        />
      </div>
    );
  };

  const renderUnifiedDiff = () => {
    if (!visualDiff || visualDiff.length === 0) {
      // Fallback to simple change list if no visual diff
      return (
        <div className="simple-diff">
          {changes.map((change, idx) => (
            <div key={idx} className="diff-section">
              <div 
                className="diff-header"
                onClick={() => toggleSection(idx)}
              >
                <div className="header-left">
                  <button className="expand-button">
                    {expandedSections.has(idx) ? 
                      <ChevronDown size={16} /> : 
                      <ChevronRight size={16} />
                    }
                  </button>
                  <span className="line-info">
                    Lines {change.line_start}-{change.line_end}
                  </span>
                  {getRiskIcon(change.risk_level)}
                  <span className={`change-type ${change.risk_level}`}>
                    {getChangeTypeLabel(change.change_type)}
                  </span>
                </div>
                <div className="change-reason">
                  {change.reason}
                </div>
              </div>
              
              {expandedSections.has(idx) && (
                <div className="diff-content">
                  <div className="code-block removed">
                    <div className="diff-line-header">- Original</div>
                    <pre><code>{change.original_code}</code></pre>
                  </div>
                  <div className="code-block added">
                    <div className="diff-line-header">+ Adapted</div>
                    <pre><code>{change.adapted_code}</code></pre>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      );
    }

    // Render visual diff if available
    return (
      <div className="visual-diff">
        {visualDiff.map((hunk, idx) => (
          <div key={idx} className="diff-hunk">
            {!hunk.header.startsWith('@@') && (
              <div className="hunk-header">{hunk.header}</div>
            )}
            <div className="hunk-lines">
              {hunk.lines.map((line: any, lineIdx: number) => (
                <div 
                  key={lineIdx} 
                  className={`diff-line ${line.type}`}
                >
                  <span className="line-number">
                    {line.number || ' '}
                  </span>
                  <span className="line-content">
                    {line.type === 'removed' && '- '}
                    {line.type === 'added' && '+ '}
                    {line.type === 'unchanged' && '  '}
                    {line.content}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderSplitDiff = () => {
    const originalLines = originalCode.split('\n');
    const adaptedLines = adaptedCode.split('\n');
    
    return (
      <div className="split-diff">
        <div className="split-side original">
          <div className="split-header">Original Code</div>
          <div className="code-lines">
            {originalLines.map((line, idx) => (
              <div key={idx} className="code-line">
                <span className="line-number">{idx + 1}</span>
                <span className="line-content">{line}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="split-side adapted">
          <div className="split-header">Adapted Code</div>
          <div className="code-lines">
            {adaptedLines.map((line, idx) => (
              <div key={idx} className="code-line">
                <span className="line-number">{idx + 1}</span>
                <span className="line-content">{line}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="code-diff-viewer">
      <div className="diff-header-bar">
        <div className="preservation-indicator">
          <div className="preservation-bar">
            <div 
              className="preservation-fill"
              style={{ width: `${preservationRatio}%` }}
            />
          </div>
          <span className="preservation-text">
            {preservationRatio}% Code Preserved
          </span>
        </div>
        
        <div className="view-controls">
          <div className="view-mode-toggle">
            <button
              className={`mode-button ${viewMode === 'unified' ? 'active' : ''}`}
              onClick={() => setViewMode('unified')}
            >
              Unified
            </button>
            <button
              className={`mode-button ${viewMode === 'split' ? 'active' : ''}`}
              onClick={() => setViewMode('split')}
            >
              Split
            </button>
            <button
              className={`mode-button ${viewMode === 'aligned' ? 'active' : ''}`}
              onClick={() => setViewMode('aligned')}
            >
              Aligned
            </button>
          </div>
        </div>
      </div>

      <div className="diff-summary">
        <div className="summary-item">
          <span className="summary-icon">+{diffStats.additions}</span>
          <span className="summary-label">additions</span>
        </div>
        <div className="summary-item">
          <span className="summary-icon">-{diffStats.deletions}</span>
          <span className="summary-label">deletions</span>
        </div>
        <div className="summary-item">
          <span className="summary-icon">~{diffStats.modifications}</span>
          <span className="summary-label">modifications</span>
        </div>
        <div className="summary-divider">|</div>
        <div className="summary-item">
          <CheckCircle size={16} className="summary-icon preserved" />
          <span className="risk-count low">{changes.filter(c => c.risk_level === 'low').length} Low Risk</span>
        </div>
        <div className="summary-item">
          <AlertCircle size={16} className="summary-icon warning" />
          <span className="risk-count medium">{changes.filter(c => c.risk_level === 'medium').length} Medium Risk</span>
        </div>
        <div className="summary-item">
          <AlertTriangle size={16} className="summary-icon danger" />
          <span className="risk-count high">{changes.filter(c => c.risk_level === 'high').length} High Risk</span>
        </div>
      </div>

      <div className="diff-content-area">
        {viewMode === 'unified' && renderUnifiedDiff()}
        {viewMode === 'split' && renderSplitDiff()}
        {viewMode === 'aligned' && renderAlignedDiff()}
      </div>
    </div>
  );
};

// Main component wrapped with error boundary
export const CodeDiffViewer: React.FC<CodeDiffViewerProps> = (props) => {
  return (
    <DiffErrorBoundary>
      <CodeDiffViewerContent {...props} />
    </DiffErrorBoundary>
  );
};